import React, { useState, useEffect } from 'react';

interface EmailVerificationProps {
  email: string;
  onVerificationSuccess: () => void;
  onCancel: () => void;
}

export default function EmailVerification({ 
  email, 
  onVerificationSuccess,
  onCancel 
}: EmailVerificationProps) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [canResend, setCanResend] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [attemptsLeft, setAttemptsLeft] = useState(3);

  // Change this to your Render URL after deployment
  // For local testing: http://localhost:3001
  // For production: https://your-app.onrender.com
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  // Send verification code when component mounts
  useEffect(() => {
    sendVerificationCode();
  }, []);

  const sendVerificationCode = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/api/send-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Verification code sent! Check your email.');
        setCountdown(60);
        setCanResend(false);
        setAttemptsLeft(3);
      } else {
        setError(data.error || 'Failed to send code');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      console.error('Send code error:', err);
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('✅ Email verified successfully!');
        setTimeout(() => {
          onVerificationSuccess();
        }, 1000);
      } else {
        setError(data.error || 'Invalid code');
        if (data.attemptsLeft !== undefined) {
          setAttemptsLeft(data.attemptsLeft);
        }
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Verify code error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    setCode('');
    setError('');
    setSuccess('');
    sendVerificationCode();
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '450px',
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        padding: '40px'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{ fontSize: '64px', marginBottom: '10px' }}>🎅</div>
          <h2 style={{ margin: '0 0 10px 0', color: '#C71212', fontSize: '28px' }}>
            Verify Your Email
          </h2>
          <p style={{ color: '#666', fontSize: '15px', margin: 0 }}>
            We've sent a 6-digit code to
          </p>
          <p style={{ color: '#333', fontSize: '16px', fontWeight: 'bold', margin: '5px 0 0 0' }}>
            {email}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={verifyCode}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#333',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              Verification Code
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              required
              disabled={loading}
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '28px',
                textAlign: 'center',
                letterSpacing: '10px',
                fontFamily: "'Courier New', monospace",
                fontWeight: 'bold',
                border: '2px solid #ddd',
                borderRadius: '10px',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#109433'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
            />
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '8px',
              fontSize: '12px',
              color: '#666'
            }}>
              <span>Enter the 6-digit code</span>
              <span style={{ color: attemptsLeft <= 1 ? '#C71212' : '#666' }}>
                {attemptsLeft} {attemptsLeft === 1 ? 'attempt' : 'attempts'} left
              </span>
            </div>
          </div>

          {/* Verify Button */}
          <button
            type="submit"
            disabled={loading || code.length !== 6}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: code.length === 6 && !loading ? '#109433' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: code.length === 6 && !loading ? 'pointer' : 'not-allowed',
              transition: 'background-color 0.2s',
              marginBottom: '12px'
            }}
            onMouseOver={(e) => {
              if (code.length === 6 && !loading) {
                e.currentTarget.style.backgroundColor = '#0d7a2a';
              }
            }}
            onMouseOut={(e) => {
              if (code.length === 6 && !loading) {
                e.currentTarget.style.backgroundColor = '#109433';
              }
            }}
          >
            {loading ? 'Verifying...' : 'Verify Code'}
          </button>

          {/* Resend and Cancel Buttons */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={handleResend}
              disabled={!canResend || loading}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: canResend && !loading ? '#007bff' : '#e0e0e0',
                color: canResend && !loading ? 'white' : '#999',
                border: 'none',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: canResend && !loading ? 'pointer' : 'not-allowed',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => {
                if (canResend && !loading) {
                  e.currentTarget.style.backgroundColor = '#0056b3';
                }
              }}
              onMouseOut={(e) => {
                if (canResend && !loading) {
                  e.currentTarget.style.backgroundColor = '#007bff';
                }
              }}
            >
              {canResend ? '🔄 Resend Code' : `Resend (${countdown}s)`}
            </button>

            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: loading ? '#e0e0e0' : '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => {
                if (!loading) {
                  e.currentTarget.style.backgroundColor = '#545b62';
                }
              }}
              onMouseOut={(e) => {
                if (!loading) {
                  e.currentTarget.style.backgroundColor = '#6c757d';
                }
              }}
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Error Message */}
        {error && (
          <div style={{
            marginTop: '20px',
            padding: '14px',
            backgroundColor: '#fff5f5',
            border: '2px solid #C71212',
            borderRadius: '8px',
            color: '#C71212',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '18px' }}>⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div style={{
            marginTop: '20px',
            padding: '14px',
            backgroundColor: '#f0fdf4',
            border: '2px solid #109433',
            borderRadius: '8px',
            color: '#109433',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '18px' }}>✅</span>
            <span>{success}</span>
          </div>
        )}

        {/* Info Box */}
        <div style={{
          marginTop: '20px',
          padding: '16px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#495057',
          borderLeft: '4px solid #109433'
        }}>
          <div style={{ marginBottom: '8px' }}>
            <strong>⏰ Code expires in 10 minutes</strong>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <strong>📧 Check your spam folder</strong> if you don't see it
          </div>
          <div>
            <strong>🔒 Your code is secure</strong> and can only be used once
          </div>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: '30px',
          textAlign: 'center',
          fontSize: '12px',
          color: '#999'
        }}>
          <p style={{ margin: 0 }}>🎅 Secret Santa Email Verification</p>
          <p style={{ margin: '5px 0 0 0' }}>Powered by Resend</p>
        </div>
      </div>
    </div>
  );
}